class FeaturedProductCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('[data-carousel-track]');
    this.slides = Array.from(container.querySelectorAll('.featured-carousel-slide'));
    this.prevBtn = container.querySelector('.carousel-nav-prev');
    this.nextBtn = container.querySelector('.carousel-nav-next');
    this.dotsContainer = container.querySelector('[data-carousel-dots]');
    this.currentIndex = 0;
    this.isScrolling = false;
    this.scrollTimeout = null;

    if (!this.track || this.slides.length === 0) return;

    this.init();
  }

  init() {
    this.setupNavigation();
    this.setupDots();
    this.setupTouchScroll();
    this.updateNavigationState();
    
    // Handle window resize
    this.resizeObserver = new ResizeObserver(() => {
      this.updateNavigationState();
    });
    this.resizeObserver.observe(this.container);
  }

  setupNavigation() {
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollToPrevious());
      this.nextBtn.addEventListener('click', () => this.scrollToNext());
    }
  }

  setupDots() {
    if (!this.dotsContainer) return;

    // Create dots
    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.classList.add('carousel-dot');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      if (index === 0) dot.classList.add('active');
      
      dot.addEventListener('click', () => {
        this.scrollToSlide(index);
      });
      
      this.dotsContainer.appendChild(dot);
    });

    this.dots = Array.from(this.dotsContainer.querySelectorAll('.carousel-dot'));
  }

  setupTouchScroll() {
    // Track scroll position to update current slide
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimeout);
      this.isScrolling = true;

      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
        this.updateCurrentSlide();
      }, 150);
    });
  }

  updateCurrentSlide() {
    const scrollLeft = this.track.scrollLeft;
    const slideWidth = this.slides[0].offsetWidth;
    const gap = this.getGapSize();
    
    // Calculate which slide is most visible
    this.currentIndex = Math.round(scrollLeft / (slideWidth + gap));
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.slides.length - 1));
    
    this.updateDots();
    this.updateNavigationState();
  }

  updateDots() {
    if (!this.dots) return;
    
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }

  updateNavigationState() {
    if (!this.prevBtn || !this.nextBtn) return;

    const isAtStart = this.track.scrollLeft <= 0;
    const isAtEnd = this.track.scrollLeft >= (this.track.scrollWidth - this.track.clientWidth - 1);

    this.prevBtn.style.opacity = isAtStart ? '0.3' : '1';
    this.prevBtn.style.pointerEvents = isAtStart ? 'none' : 'auto';
    
    this.nextBtn.style.opacity = isAtEnd ? '0.3' : '1';
    this.nextBtn.style.pointerEvents = isAtEnd ? 'none' : 'auto';
  }

  scrollToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    const slideWidth = this.slides[0].offsetWidth;
    const gap = this.getGapSize();
    const scrollPosition = index * (slideWidth + gap);

    this.track.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });

    this.currentIndex = index;
    this.updateDots();
  }

  scrollToPrevious() {
    const newIndex = Math.max(0, this.currentIndex - 1);
    this.scrollToSlide(newIndex);
  }

  scrollToNext() {
    const newIndex = Math.min(this.slides.length - 1, this.currentIndex + 1);
    this.scrollToSlide(newIndex);
  }

  getGapSize() {
    const computedStyle = window.getComputedStyle(this.container);
    const gap = computedStyle.getPropertyValue('--carousel-gap');
    return parseInt(gap) || 16;
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

// Initialize all carousels on the page
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.featured-carousel-wrapper');
  carousels.forEach(carousel => new FeaturedProductCarousel(carousel));
});

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('.featured-carousel-wrapper');
    if (carousel) {
      new FeaturedProductCarousel(carousel);
    }
  });
}

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FeaturedProductCarousel;
}